import './index.less';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToggle, useWindowSize } from 'react-use';

import { DARK_ROUTER_PATHS } from '@/shared/constants/nav-constants';
import DesktopHeader from './Desktop';
import { EventSource } from '@/shared/constants/event';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { goToContactSales } from '@/shared/utils/utils';
import { logCommonView } from '@/lib/service';
import { useConfigProvider } from '@/lib/context';
import { useRouter } from 'next/router';

const MobileHeader = dynamic(() => import('@/components/Common/Layout/Header/Mobile'), { ssr: false });

interface IProps {
  isLarge?: boolean;
  showIntercom: () => void;
  hideIntercom: () => void;
  isFullLength?: boolean;
}

export default function Header({ isLarge = false, isFullLength = false, showIntercom, hideIntercom }: IProps) {
  const { isMobile } = useConfigProvider();
  const { width } = useWindowSize();
  const router = useRouter();
  const pietraHeaderRef = useRef<HTMLDivElement>(null);
  const [openLeftMenuDrawer, openLeftMenuDrawerFn] = useToggle(false);
  const [mobileNavTheme, setMobileNavTheme] = useState<'light' | 'dark'>('light');
  const [showMobileHeader, setShowMobileHeader] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false);

  const goToPreviewFlow = useCallback(() => {
    logCommonView(EventSource.BOOK_A_DEMO, EventSource.BOOK_A_DEMO, isMobile);
    goToContactSales();
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const _isMobile = isMobile || ((window.document.body.offsetWidth || width) ?? 1281) < 1024;

    const htmlElement = window.document.querySelector('html');
    if (!htmlElement) return;
    const setHeaderClass = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const headerContainer = document.querySelector('.pietra-header-container');
      if (scrollTop > 0) {
        if (!_isMobile && headerContainer) {
          headerContainer?.classList?.add('sticky-header');
        }
        pietraHeaderRef.current?.classList.add('pietra-sticky-header');
      } else {
        if (!_isMobile && headerContainer) {
          headerContainer?.classList?.remove('sticky-header');
        }
        pietraHeaderRef.current?.classList.remove('pietra-sticky-header');
      }
    };

    window.addEventListener('scroll', setHeaderClass);
    window.addEventListener('resize', setHeaderClass);

    const setTheme = (theme: string) => htmlElement.setAttribute('data-theme', theme);

    const theme = DARK_ROUTER_PATHS.some((path) => router.asPath === path) ? 'dark' : 'light';

    if (_isMobile) {
      setMobileNavTheme(theme);
    } else {
      setTheme(theme);
    }
    setShowMobileNav(_isMobile);
    setShowMobileHeader(_isMobile);
    return () => {
      window.removeEventListener('scroll', setHeaderClass);
      window.removeEventListener('resize', setHeaderClass);
    };
  }, [isMobile, router.asPath, width]);

  useEffect(() => {
    if (openLeftMenuDrawer) {
      hideIntercom();
    } else {
      showIntercom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLeftMenuDrawer]);

  const mobileHeader = useMemo(
    () => (
      <MobileHeader
        openLeftMenuDrawer={openLeftMenuDrawer}
        openLeftMenuDrawerFn={openLeftMenuDrawerFn}
        goToPreviewFlow={goToPreviewFlow}
        breakpoint={width ?? 1281}
      />
    ),
    [goToPreviewFlow, openLeftMenuDrawer, openLeftMenuDrawerFn, width],
  );

  const defaultHeader = useMemo(
    () => <DesktopHeader isLarge={isLarge} isFullLength={isFullLength} goToPreviewFlow={goToPreviewFlow} />,
    [goToPreviewFlow, isFullLength, isLarge],
  );

  const headerContainer = useMemo(
    () => (showMobileHeader ? mobileHeader : defaultHeader),
    [showMobileHeader, mobileHeader, defaultHeader],
  );

  return (
    <div ref={pietraHeaderRef} className={classNames('pietra-header', mobileNavTheme, { mobile: showMobileNav })}>
      {headerContainer}
    </div>
  );
}
